import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { DecisionConditionSchema } from "./decision.step";

/**
 * A single case in the switch step configuration.
 * Each case has a label (becomes the output socket ID) and a condition to evaluate.
 */
export const SwitchCaseSchema = z.object({
  label: z
    .string()
    .regex(
      /^[a-z][a-z0-9_]*$/,
      "Label must be lowercase alphanumeric with underscores (valid socket ID)",
    )
    .describe("Branch name — becomes the output socket ID"),
  condition: DecisionConditionSchema.describe(
    "Condition to evaluate against the input value",
  ),
});

export type SwitchCase = z.infer<typeof SwitchCaseSchema>;

/**
 * Switch Step
 *
 * Multi-branch conditional routing. Evaluates an ordered list of conditions
 * against an input value and routes to the first matching case's output socket.
 * All non-matching cases (and optionally a default) receive SOCKET_NOT_TAKEN,
 * triggering skip propagation on their downstream branches.
 *
 * Output sockets are generated dynamically at template instantiation time
 * based on the configured cases — similar to how event_trigger generates
 * outputs from the selected event schema.
 *
 * Example workflow:
 * ```
 * ClassifyDocument → Switch(cases: [
 *                     { label: "invoice", condition: { operator: "equals", value: "invoice" } },
 *                     { label: "po",      condition: { operator: "equals", value: "purchase_order" } },
 *                     { label: "receipt",  condition: { operator: "equals", value: "receipt" } },
 *                   ])
 *                     ├─ [invoice] → ProcessInvoice
 *                     ├─ [po]      → ProcessPO
 *                     ├─ [receipt]  → ProcessReceipt
 *                     └─ [default]  → ManualReview
 * ```
 *
 * First match wins — evaluation stops at the first satisfied condition.
 * If no case matches and `hasDefault` is true, the `default` socket is taken.
 * With `multiMatch` enabled, every case is evaluated independently and each
 * matching case's socket is taken (default still only fires on zero matches).
 *
 * Gating a branch whose data comes from elsewhere: wire the case socket into
 * the target step's flow-control anchor (`__flow_control_in__`) and wire the
 * real data (e.g. the inbound email) directly from its producer. An untaken
 * case emits SOCKET_NOT_TAKEN, which dead-prunes any step whose
 * flow-control-in edge it feeds — so the switch decides WHICH branch runs
 * while each branch's data flows on ordinary edges.
 */
export const switchStep = defineStepConfig({
  type: "switch",
  name: "Switch",
  description: "Multi-branch conditional routing based on ordered conditions",
  category: "Control Flow",

  inputSchema: z.object({
    value: z
      .any()
      .describe("The value to evaluate against each case condition"),
  }),

  // Output sockets are generated dynamically at instantiation from config.cases.
  // Uses z.record() so that dynamic case keys survive Zod validation (z.object()
  // would strip unknown keys). The real socket definitions come from
  // instantiateTemplate's dynamic generation.
  outputSchema: z.record(z.string(), z.any()),

  configSchema: z.object({
    cases: z
      .array(SwitchCaseSchema)
      .min(1)
      .describe("Ordered list of cases to evaluate (first match wins)"),
    hasDefault: z
      .boolean()
      .default(true)
      .describe("Whether to include a default output for unmatched values"),
    multiMatch: z
      .boolean()
      .optional()
      .describe(
        "Evaluate every case independently — each matching case's socket is taken (default: first match wins). The `value` input becomes an array socket so an upstream array feeds the switch whole instead of fanning out.",
      ),
  }),

  emits: [] as const,
});
